/**
 * personApi.js — thin, typed-ish wrappers over the Brain / Person endpoints.
 *
 * Contract: BRAIN_PERSON_CONTRACT.md §3.2 (dossier), §3.3 (read), §3.5 (hour).
 *
 * Rules honoured here:
 *  - Read-only. No writes of any kind.
 *  - Every wrapper returns `res.data.data` on success.
 *  - Failures THROW. We never swallow an error into a silent empty state
 *    (§2.9: "the `.catch(()=>{})` pattern used elsewhere in DashboardV2 is a
 *    known bug — do not copy it").
 */

import { apiInstance as api } from "../../../../../hooks/useApi";

const BASE = "/api/v1/brain-person";

/**
 * Normalises whatever the caller passes as a range into the contract's
 * `{ start, end, timezone }` body. Accepts `{ start, end }` or
 * `{ startDate, endDate }` interchangeably.
 */
/**
 * Callers disagree on shape: PersonPanel calls `fetch*(userId, range, opts)`
 * positionally, PersonBrain calls `fetch*({ userId, range, signal })`. Accept
 * both rather than making either caller wrong — passing the object as `userId`
 * silently produced a `[object Object]` URL and an empty body, which the route
 * rejected with a 400.
 */
function argsOf(userId, range, opts) {
  if (userId && typeof userId === "object" && !Array.isArray(userId)) {
    const a = userId;
    return {
      userId: a.userId || a.id || null,
      // Allow both `{range:{...}}` and a flattened `{start,end,timezone,...}`.
      range: a.range || a,
      opts: opts || (a.signal ? { signal: a.signal } : undefined),
    };
  }
  return { userId, range, opts };
}

function normalizeRange(range) {
  const r = range || {};
  const start = r.start != null ? r.start : r.startDate;
  const end = r.end != null ? r.end : r.endDate;
  const body = {};
  if (start != null) body.start = start;
  if (end != null) body.end = end;
  if (r.timezone) body.timezone = r.timezone;
  if (r.refresh) body.refresh = true;
  return body;
}

/**
 * Builds a real Error out of an axios failure, preserving the contract's
 * `{ success:false, error:{ code, message } }` shape when present.
 */
function toError(err, fallbackMessage) {
  const payload = err && err.response && err.response.data;
  const inner = payload && payload.error;
  const message =
    (inner && inner.message) ||
    (payload && payload.message) ||
    (err && err.message) ||
    fallbackMessage;
  const error = new Error(message || fallbackMessage);
  error.code = (inner && inner.code) || (payload && payload.code) || "INTERNAL";
  error.status = (err && err.response && err.response.status) || 0;
  error.cause = err;
  return error;
}

/**
 * Asserts the envelope really is `{ success:true, data:{...} }` and hands back
 * `data`. A 200 with `success:false` is still a failure.
 */
function unwrap(res, fallbackMessage) {
  const payload = res && res.data;
  if (!payload || payload.success === false) {
    const inner = payload && payload.error;
    const error = new Error(
      (inner && inner.message) || (payload && payload.message) || fallbackMessage
    );
    error.code = (inner && inner.code) || "INTERNAL";
    error.status = (res && res.status) || 200;
    throw error;
  }
  if (payload.data === undefined || payload.data === null) {
    const error = new Error(fallbackMessage);
    error.code = "INTERNAL";
    error.status = (res && res.status) || 200;
    throw error;
  }
  return payload.data;
}

/**
 * POST /api/v1/brain-person/:userId/dossier — §3.2
 * The deterministic, zero-LLM data spine: metrics, projects, timeline, signals.
 *
 * @param {string} userId
 * @param {{ start?, end?, startDate?, endDate?, timezone?, includeTimeline?, refresh? }} range
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<object>} the Dossier (§1.4)
 */
export async function fetchDossier(userId, range, opts) {
  ({ userId, range, opts } = argsOf(userId, range, opts));
  if (!userId) throw new Error("fetchDossier: userId is required");
  const body = normalizeRange(range);
  if (range && range.includeTimeline === false) body.includeTimeline = false;
  try {
    const res = await api.post(
      `${BASE}/${encodeURIComponent(userId)}/dossier`,
      body,
      opts && opts.signal ? { signal: opts.signal } : undefined
    );
    return unwrap(res, "Could not load this person's activity dossier.");
  } catch (err) {
    if (err && err.code && err.status !== undefined) throw err;
    throw toError(err, "Could not load this person's activity dossier.");
  }
}

/**
 * POST /api/v1/brain-person/:userId/read — §3.3 (Band 1)
 * One non-streaming Haiku call -> `{ sentences, tone, signalIds, degraded, generatedAt }`.
 * The client never uploads a dossier; the server rebuilds/reuses its own.
 *
 * @param {string} userId
 * @param {{ start?, end?, startDate?, endDate?, timezone?, refresh? }} range
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<object>} the read payload
 */
export async function fetchRead(userId, range, opts) {
  ({ userId, range, opts } = argsOf(userId, range, opts));
  if (!userId) throw new Error("fetchRead: userId is required");
  const body = normalizeRange(range);
  try {
    const res = await api.post(
      `${BASE}/${encodeURIComponent(userId)}/read`,
      body,
      opts && opts.signal ? { signal: opts.signal } : undefined
    );
    return unwrap(res, "Could not generate the read for this person.");
  } catch (err) {
    if (err && err.code && err.status !== undefined) throw err;
    throw toError(err, "Could not generate the read for this person.");
  }
}

/**
 * GET /api/v1/brain-person/:userId/hour/:ts — §3.5 (Strip expansion)
 * `ts` is the integer epoch **ms** of the local hour start; the server snaps it
 * down to the hour boundary in the org timezone.
 *
 * @param {string} userId
 * @param {number|string} ts epoch ms
 * @param {{ limit?: number, clusters?: number, titles?: string, signal?: AbortSignal }} [opts]
 * @returns {Promise<object>} the HourDetail (clusters + evidence for that hour)
 */
export async function fetchHour(userId, ts, opts) {
  if (userId && typeof userId === "object" && !Array.isArray(userId)) {
    const a = userId;
    opts = opts || { signal: a.signal };
    ts = ts != null ? ts : (a.ts != null ? a.ts : a.hourStartMs);
    userId = a.userId || a.id;
  }
  if (!userId) throw new Error("fetchHour: userId is required");
  const stamp = Number(ts);
  if (!Number.isFinite(stamp)) throw new Error("fetchHour: ts must be epoch ms");
  const o = opts || {};
  const params = {};
  if (o.limit != null) params.limit = o.limit;
  if (o.clusters != null) params.clusters = o.clusters;
  if (o.titles) params.titles = o.titles;

  const config = {};
  if (Object.keys(params).length) config.params = params;
  if (o.signal) config.signal = o.signal;

  try {
    const res = await api.get(
      `${BASE}/${encodeURIComponent(userId)}/hour/${Math.trunc(stamp)}`,
      config
    );
    return unwrap(res, "Could not load the detail for this hour.");
  } catch (err) {
    if (err && err.code && err.status !== undefined) throw err;
    throw toError(err, "Could not load the detail for this hour.");
  }
}

const personApi = { fetchDossier, fetchRead, fetchHour };
export default personApi;
